/* AZAN particle logo — 2D canvas effect.
   Samples the AS-Logo-Kopf SVG into a particle field, lets particles
   spring back to their home pixel coordinates, and repels them from the
   pointer. No WebGL — plain Canvas2D with additive blending for a glow. */

const CANVAS_SELECTOR = '[data-azan-particle-logo]';
const instances = new WeakMap();

const STRIDE = 5;             /* pixel sampling step — lower = more particles */
const PARTICLE_SIZE_MIN = 0.55;
const PARTICLE_SIZE_MAX = 1.35;
const ALPHA_THRESHOLD = 140;
const EDGE_PROBE = 7;         /* px distance to check for outside-of-shape neighbour */
const INTERIOR_KEEP = 0.05;   /* fraction of interior particles to retain */
const SPRING = 0.06;
const DAMPING = 0.86;
const MOUSE_RADIUS = 110;
const MOUSE_FORCE = 26;
const RETURN_NOISE = 0.18;

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

async function fetchSvgImage(url) {
  /* Fetch the SVG text then wrap as a data URL so the rasterized canvas
     stays same-origin and getImageData() doesn't throw a security error. */
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) throw new Error('logo fetch failed');
  const text = await response.text();
  const encoded = encodeURIComponent(text)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');
  const img = new Image();
  img.decoding = 'async';
  img.crossOrigin = 'anonymous';
  img.src = `data:image/svg+xml;charset=utf-8,${encoded}`;
  await img.decode();
  return img;
}

/* Sprite removed — we now draw all particles in one batched arc/fill pass
   for performance. The additive blend mode supplies the glow naturally
   where particles overlap. */

function sampleParticles(image, width, height) {
  const aspect = image.naturalWidth / image.naturalHeight;
  /* Fit the logo into ~55% of the canvas so there's always a wide safe
     margin on every side — particles pushed by the cursor have room to
     fly without looking cut off at the canvas edge. */
  let drawW = width * 0.55;
  let drawH = drawW / aspect;
  if (drawH > height * 0.7) {
    drawH = height * 0.7;
    drawW = drawH * aspect;
  }
  const drawX = (width - drawW) / 2;
  const drawY = (height - drawH) / 2;

  const off = document.createElement('canvas');
  off.width = Math.max(1, Math.ceil(drawW));
  off.height = Math.max(1, Math.ceil(drawH));
  const octx = off.getContext('2d');
  octx.drawImage(image, 0, 0, off.width, off.height);
  const data = octx.getImageData(0, 0, off.width, off.height).data;

  const W = off.width;
  const H = off.height;
  /* A pixel is an edge particle if any of its 8 neighbours at EDGE_PROBE
     distance falls outside the shape (alpha below threshold or off the
     canvas). Interior particles are thinned via INTERIOR_KEEP so the
     silhouette stays sharp but the inside reads as sparse texture. */
  const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]];
  const isEdge = (px, py) => {
    for (let d = 0; d < dirs.length; d += 1) {
      const nx = px + dirs[d][0] * EDGE_PROBE;
      const ny = py + dirs[d][1] * EDGE_PROBE;
      if (nx < 0 || nx >= W || ny < 0 || ny >= H) return true;
      if (data[(ny * W + nx) * 4 + 3] < ALPHA_THRESHOLD) return true;
    }
    return false;
  };

  const particles = [];
  for (let py = 0; py < H; py += STRIDE) {
    for (let px = 0; px < W; px += STRIDE) {
      const alpha = data[(py * W + px) * 4 + 3];
      if (alpha < ALPHA_THRESHOLD) continue;
      if (!isEdge(px, py) && Math.random() > INTERIOR_KEEP) continue;
      const hx = drawX + px;
      const hy = drawY + py;
      /* Start already at the home pixel — no opening explosion. */
      particles.push({
        hx, hy,
        x: hx,
        y: hy,
        vx: 0,
        vy: 0,
        size: PARTICLE_SIZE_MIN + Math.random() * (PARTICLE_SIZE_MAX - PARTICLE_SIZE_MIN),
        phase: Math.random() * Math.PI * 2
      });
    }
  }
  return particles;
}

function initCanvas(canvas) {
  if (instances.has(canvas)) instances.get(canvas).destroy();

  const ctx = canvas.getContext('2d');
  let particles = [];
  let image = null;
  let width = 0;
  let height = 0;
  let dpr = 1;
  let raf = 0;
  let destroyed = false;
  /* Nothing runs until the canvas comes within one viewport of the screen
     (startObserver below): no SVG fetch, no sampling, no rAF loop. The
     160px visibility observer then runs the loop only while the canvas is
     on or near the screen. Without IntersectionObserver everything starts
     at once and stays on, as before. */
  const hasIO = 'IntersectionObserver' in window;
  let started = false;
  let visible = !hasIO;

  const mouse = { x: -9999, y: -9999, active: false };

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = Math.max(1, Math.floor(rect.width));
    height = Math.max(1, Math.floor(rect.height));
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (image) particles = sampleParticles(image, width, height);
  };

  const onPointerMove = (event) => {
    const rect = canvas.getBoundingClientRect();
    const point = event.touches ? event.touches[0] : event;
    mouse.x = point.clientX - rect.left;
    mouse.y = point.clientY - rect.top;
    mouse.active = true;
  };

  const onPointerLeave = () => {
    mouse.active = false;
    mouse.x = -9999;
    mouse.y = -9999;
  };

  const render = (now) => {
    if (destroyed) return;
    /* Off screen: stop the loop instead of idling through empty frames.
       The visibility observer restarts it on re-entry. Particles hold
       their state and the drift uses the absolute rAF timestamp, so a
       resumed loop continues exactly as an idling one would have. */
    if (!visible) {
      raf = 0;
      return;
    }
    raf = requestAnimationFrame(render);

    /* Fully clear each frame — cheaper than a destination-out alpha decay
       and keeps motion crisp. Canvas stays transparent. */
    ctx.clearRect(0, 0, width, height);

    const r2 = MOUSE_RADIUS * MOUSE_RADIUS;
    const time = now * 0.001;

    for (let i = 0; i < particles.length; i += 1) {
      const p = particles[i];
      /* Spring back to home + per-particle idle drift. */
      const drift = Math.sin(time * 0.9 + p.phase) * RETURN_NOISE;
      let fx = (p.hx - p.x) * SPRING + drift * 0.4;
      let fy = (p.hy - p.y) * SPRING + Math.cos(time * 0.7 + p.phase) * RETURN_NOISE * 0.4;

      if (mouse.active) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < r2 && d2 > 1) {
          const d = Math.sqrt(d2);
          const f = (1 - d / MOUSE_RADIUS);
          const push = f * f * MOUSE_FORCE;
          fx += (dx / d) * push;
          fy += (dy / d) * push;
        }
      }

      p.vx = (p.vx + fx) * DAMPING;
      p.vy = (p.vy + fy) * DAMPING;
      p.x += p.vx;
      p.y += p.vy;
    }

    /* Single batched arc/fill for every particle. Additive blending
       provides bloom in overlap zones, but there's only one path build
       and one fill call per frame — orders of magnitude cheaper than
       per-particle drawImage. */
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = 'rgba(255, 188, 168, 0.62)';
    ctx.beginPath();
    for (let i = 0; i < particles.length; i += 1) {
      const p = particles[i];
      ctx.moveTo(p.x + p.size, p.y);
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    }
    ctx.fill();
  };

  const startLoop = () => {
    if (destroyed || !image || !visible || raf) return;
    raf = requestAnimationFrame(render);
  };

  /* Before the start there is nothing to size: resize() runs once the
     logo has loaded, and size changes after that re-sample as before. */
  const onResize = () => {
    if (started) resize();
  };
  const onWindowResize = () => onResize();
  window.addEventListener('resize', onWindowResize);
  canvas.addEventListener('pointermove', onPointerMove, { passive: true });
  canvas.addEventListener('pointerleave', onPointerLeave);
  canvas.addEventListener('touchmove', onPointerMove, { passive: true });
  canvas.addEventListener('touchend', onPointerLeave);

  const ro = 'ResizeObserver' in window ? new ResizeObserver(() => onResize()) : null;
  ro?.observe(canvas);

  const io = hasIO
    ? new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) startLoop();
      }, { rootMargin: '160px' })
    : null;
  io?.observe(canvas);

  const start = () => {
    if (started || destroyed) return;
    started = true;
    const url = canvas.dataset.logoUrl;
    fetchSvgImage(url)
      .then((img) => {
        if (destroyed) return;
        image = img;
        resize();
        startLoop();
      })
      .catch(() => {
        canvas.classList.add('is-fallback');
      });
  };

  const startObserver = hasIO
    ? new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        startObserver.disconnect();
        start();
      }, { rootMargin: '100% 0px' })
    : null;
  if (startObserver) startObserver.observe(canvas);
  else start();

  instances.set(canvas, {
    destroy() {
      destroyed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onWindowResize);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      canvas.removeEventListener('touchmove', onPointerMove);
      canvas.removeEventListener('touchend', onPointerLeave);
      ro?.disconnect();
      io?.disconnect();
      startObserver?.disconnect();
      instances.delete(canvas);
    }
  });
}

function initAll(root = document) {
  root.querySelectorAll(CANVAS_SELECTOR).forEach(initCanvas);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll(CANVAS_SELECTOR).forEach((canvas) => {
    instances.get(canvas)?.destroy();
  });
});
